import type { MusicSettings, VoiceoverSettings } from '@/types';

/** Computes the voiceover's audible window [start, end) in absolute timeline seconds. */
export function computeVoiceoverWindow(v: VoiceoverSettings): { start: number; end: number } | null {
  if (!v.generatedDurationSeconds) return null;
  const start = v.startAt + v.silenceBefore;
  const naturalEnd = start + Math.max(0, v.generatedDurationSeconds - v.trimStart - v.trimEnd);
  const end = v.endAt ?? naturalEnd;
  return { start, end: Math.max(start, end) };
}

/**
 * Computes the music gain at a given absolute timeline time, applying fade-in,
 * fade-out (relative to total video duration) and voiceover ducking. Returned
 * value is always >= 0 and is NOT clamped to <= 1 (callers may allow boosted gain).
 */
export function computeMusicVolumeAtTime(
  time: number,
  music: MusicSettings,
  totalDurationSeconds: number,
  voiceoverWindow: { start: number; end: number } | null
): number {
  let volume = music.volume;
  const relative = time - music.startAt;

  if (music.fadeInSeconds > 0 && relative >= 0 && relative < music.fadeInSeconds) {
    volume *= relative / music.fadeInSeconds;
  }

  const fadeOutStart = Math.max(music.startAt, totalDurationSeconds - music.fadeOutSeconds);
  if (music.fadeOutSeconds > 0 && time >= fadeOutStart) {
    const progress = (time - fadeOutStart) / music.fadeOutSeconds;
    volume *= Math.max(0, 1 - progress);
  }

  if (music.duckingEnabled && voiceoverWindow && time >= voiceoverWindow.start && time <= voiceoverWindow.end) {
    volume *= 1 - music.duckingAmount;
  }

  return Math.max(0, volume);
}
